import { Box, Typography, Tooltip, Skeleton } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { colors } from "../ui/colors";

const secondsInDay = 86400;
const secondsInHour = 3600;
const secondsInMinute = 60;

const getLeftPercentageBasedOnTime = (timestamp) => {
  const date = new Date(timestamp);
  const seconds =
    date.getHours() * secondsInHour +
    date.getMinutes() * secondsInMinute +
    date.getSeconds();
  return (seconds / secondsInDay) * 100;
};

export const TimeLine = ({
  events = [],
  barHeight = 48,
  labelHeight = 24,
  timelineColor = colors.gray[100],
  eventsColor = colors.blue.base,
  cursorColor = colors.orange.base,
  loading = false,
  followLast = false,
  onCursorChange,
  onDragStart,
}) => {
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const [cursorSec, setCursorSec] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [hovering, setHovering] = useState(false);

  // Sincronizar cursor al último evento cuando followLast está activo o en la primera carga
  useEffect(() => {
    if ((followLast || cursorSec === null) && events.length > 0 && !dragging) {
      const last = events[events.length - 1];
      const date = new Date(last.createdAt);
      const seconds =
        date.getHours() * secondsInHour +
        date.getMinutes() * secondsInMinute +
        date.getSeconds();
      setCursorSec(seconds);
    }
  }, [events, followLast, cursorSec, dragging]);

  const formatTime = (totalSeconds) => {
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = Math.floor(totalSeconds % 60);
    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const updateCursor = (clientX, rect) => {
    const x = clientX - rect.left;
    const pct = Math.min(Math.max(x / rect.width, 0), 1);
    setCursorSec(pct * secondsInDay);
    if (onCursorChange) onCursorChange(pct * secondsInDay);
  };

  const handleMouseDown = (e) => {
    if (onDragStart) onDragStart();
    const rect = e.currentTarget.getBoundingClientRect();
    updateCursor(e.clientX, rect);
    setDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    const rect = e.currentTarget.getBoundingClientRect();
    updateCursor(e.clientX, rect);
  };

  const stopDrag = () => setDragging(false);

  const handleTouchStart = (e) => {
    if (onDragStart) onDragStart();
    const rect = e.currentTarget.getBoundingClientRect();
    updateCursor(e.touches[0].clientX, rect);
    setDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!dragging) return;
    const rect = e.currentTarget.getBoundingClientRect();
    updateCursor(e.touches[0].clientX, rect);
  };

  const handleTouchEnd = () => setDragging(false);

  // Muestrea eventos para reducir cantidad de nodos DOM
  const sampledEvents = useMemo(() => {
    if (!events || events.length === 0) return [];
    const maxNodes = 400;
    if (events.length <= maxNodes) return events;
    const step = Math.ceil(events.length / maxNodes);
    return events.filter((_, idx) => idx % step === 0);
  }, [events]);

  return (
    <Box
      width="100%"
      position="relative"
      mt={3}
      mb={2}
      height={barHeight + labelHeight}
      sx={{
        userSelect: "none",
      }}
    >
      {/* Hour labels */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        height={labelHeight}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={0.5}
      >
        {hours.map((hr) => {
          const shouldShow = hr % 3 === 0;
          return (
            <Typography
              key={hr}
              variant="caption"
              sx={{
                fontSize: 11,
                fontWeight: 500,
                color: shouldShow ? colors.gray[400] : "transparent",
                userSelect: "none",
                pointerEvents: "none",
                minWidth: "24px",
                textAlign: "center",
              }}
            >
              {hr.toString().padStart(2, "0")}
            </Typography>
          );
        })}
      </Box>

      {/* Timeline bar */}
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        height={barHeight}
        sx={{
          backgroundColor: timelineColor,
          cursor: dragging ? "grabbing" : "pointer",
          borderRadius: "12px",
          border: `2px solid ${hovering || dragging ? colors.orange[100] : colors.gray[50]}`,
          transition: "all 0.2s ease",
          overflow: "hidden",
          boxShadow: dragging
            ? `0 4px 12px ${colors.orange[200]}`
            : hovering
            ? `0 2px 8px ${colors.gray[200]}`
            : "0 2px 4px rgba(12, 12, 12, 0.06)",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => {
          setHovering(false);
          stopDrag();
        }}
        onMouseUp={stopDrag}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {events.length === 0 ? (
          loading ? (
            <Skeleton
              variant="rectangular"
              animation="wave"
              width="100%"
              height="100%"
              sx={{
                borderRadius: "10px",
                animationDuration: "1.5s",
                backgroundColor: colors.gray[50],
                "&::after": {
                  animationDuration: "1.5s",
                  background: `linear-gradient(90deg, transparent, ${colors.warmWhite.base}, transparent)`,
                },
              }}
            />
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              <Typography
                variant="caption"
                sx={{
                  color: colors.gray[300],
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                No records
              </Typography>
            </Box>
          )
        ) : (
          <>
            {/* Grid de fondo para horas */}
            <Box
              position="absolute"
              top={0}
              bottom={0}
              left={0}
              right={0}
              display="flex"
            >
              {hours.map((hr) => (
                <Box
                  key={hr}
                  flex={1}
                  borderRight={
                    hr < 23 ? `1px solid ${colors.gray[50]}` : "none"
                  }
                  sx={{
                    opacity: 0.5,
                  }}
                />
              ))}
            </Box>

            {/* Render events de forma optimizada */}
            {sampledEvents.map((event, idx) => (
              <Box
                key={idx}
                position="absolute"
                top="4px"
                bottom="4px"
                width="3px"
                bgcolor={eventsColor}
                left={`${getLeftPercentageBasedOnTime(event.createdAt)}%`}
                sx={{
                  borderRadius: "2px",
                  opacity: 0.8,
                  transition: "opacity 0.2s ease",
                  "&:hover": {
                    opacity: 1,
                  },
                }}
              />
            ))}

            {/* Cursor movable line with persistent tooltip */}
            {cursorSec !== null && (
              <Tooltip
                title={
                  <Box sx={{ py: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        fontFamily: "monospace",
                      }}
                    >
                      {formatTime(cursorSec)}
                    </Typography>
                  </Box>
                }
                placement="top"
                arrow
                open
                disableFocusListener
                disableHoverListener
                disableTouchListener
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: colors.inverse.base,
                      color: colors.warmWhite.base,
                      fontSize: 13,
                      fontWeight: 600,
                      borderRadius: "8px",
                      px: 1.5,
                      py: 0.75,
                      boxShadow: `0 4px 12px rgba(12, 12, 12, 0.2)`,
                    },
                  },
                  arrow: {
                    sx: {
                      color: colors.gray.base,
                    },
                  },
                  popper: {
                    sx: { pointerEvents: "none" },
                  },
                }}
              >
                <Box
                  position="absolute"
                  top={0}
                  bottom={0}
                  width="3px"
                  bgcolor={cursorColor}
                  left={`${(cursorSec / secondsInDay) * 100}%`}
                  pointerEvents="none"
                  sx={{
                    borderRadius: "2px",
                    boxShadow: `0 0 8px ${colors.orange[300]}`,
                    zIndex: 10,
                  }}
                >
                  {/* Círculo superior */}
                  <Box
                    position="absolute"
                    top="-4px"
                    left="50%"
                    sx={{
                      transform: "translateX(-50%)",
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      bgcolor: cursorColor,
                      border: `2px solid ${colors.warmWhite.base}`,
                      boxShadow: `0 2px 6px ${colors.orange[400]}`,
                    }}
                  />
                  {/* Círculo inferior */}
                  <Box
                    position="absolute"
                    bottom="-4px"
                    left="50%"
                    sx={{
                      transform: "translateX(-50%)",
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      bgcolor: cursorColor,
                      border: `2px solid ${colors.warmWhite.base}`,
                      boxShadow: `0 2px 6px ${colors.orange[400]}`,
                    }}
                  />
                </Box>
              </Tooltip>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};